import React from 'react';
import { Box, Typography, useTheme, IconButton, CircularProgress, Fab, useMediaQuery } from '@mui/material';
import { Refresh } from '@mui/icons-material';

interface PortalScreenHeaderProps {
    title: string;
    subtitle?: string;
    icon: React.ReactNode;
    actions?: React.ReactNode;
    /** Icon to show in mobile FAB when actions are provided */
    mobileActionIcon?: React.ReactNode;
    /** Callback for mobile FAB click */
    onMobileActionClick?: () => void;
    onRefresh?: () => void;
    refreshing?: boolean;
}

export const PortalScreenHeader: React.FC<PortalScreenHeaderProps> = ({
    title,
    subtitle,
    icon,
    actions,
    mobileActionIcon,
    onMobileActionClick,
    onRefresh,
    refreshing = false
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: { xs: 2, sm: 3 },
                mt: { xs: 1, sm: 2 },
                px: 1,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 40, sm: 48 },
                        height: { xs: 40, sm: 48 },
                        borderRadius: { xs: 2, sm: 3 },
                        backgroundColor: theme.palette.mode === 'dark' 
                            ? theme.palette.primary.dark 
                            : theme.palette.primary.light,
                        color: theme.palette.primary.contrastText,
                        '& .MuiSvgIcon-root': {
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        },
                    }}
                >
                    {icon}
                </Box>
                <Box>
                    <Typography
                        variant='h4'
                        fontWeight='bold'
                        sx={{
                            color: theme.palette.text.primary,
                            fontFamily: 'primaryFont',
                            letterSpacing: 0.5,
                            textAlign: 'left',
                            fontSize: { xs: '1.5rem', sm: '2.125rem' },
                        }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{
                                fontFamily: 'primaryFont',
                                letterSpacing: 0.5,
                                textAlign: 'left',
                                display: { xs: 'none', sm: 'block' },
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {onRefresh && (
                    <IconButton
                        onClick={onRefresh}
                        disabled={refreshing}
                        sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.08)'
                                : 'rgba(0, 0, 0, 0.04)',
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.12)'
                                    : 'rgba(0, 0, 0, 0.08)',
                            },
                        }}
                    >
                        {refreshing ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: theme.palette.primary.main }}
                            />
                        ) : (
                            <Refresh />
                        )}
                    </IconButton>
                )}
                
                {/* Mobile: Show FAB icon button */}
                {isMobile && mobileActionIcon && onMobileActionClick ? (
                    <Fab
                        size='small'
                        color='primary'
                        onClick={onMobileActionClick}
                        sx={{
                            boxShadow: theme.palette.mode === 'dark'
                                ? '0 2px 8px rgba(0, 0, 0, 0.4)'
                                : '0 2px 8px rgba(0, 0, 0, 0.15)',
                            width: 40,
                            height: 40,
                            minHeight: 40,
                        }}
                    >
                        {mobileActionIcon}
                    </Fab>
                ) : (
                    /* Desktop: Show full actions */
                    actions
                )}
            </Box>
        </Box>
    );
};
